import { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { sectionViewport } from '../animations';

const easeEditorial = [0.2, 0.6, 0.2, 1] as const;

export type FlowLineProps = {
  d: string;
  dot?: boolean;
  dotColor?: 'emerald' | 'ink';
  strokeClassName?: string;
  durationMs?: number;
  delay?: number;
  className?: string;
  viewBox?: string;
};

/**
 * SVG hairline path that draws itself once on inView (pathLength 0->1) —
 * the citation-to-source thread and the how-it-works pipeline. An
 * optional single dot travels the path once and STOPS. The page's core
 * trust gesture: provenance made visible.
 *
 * Under prefers-reduced-motion the finished path is shown with no dot.
 */
export function FlowLine({
  d,
  dot = false,
  dotColor = 'emerald',
  strokeClassName = 'stroke-emerald-600',
  durationMs = 700,
  delay = 0,
  className,
  viewBox = '0 0 100 100',
}: FlowLineProps) {
  const reduce = useReducedMotion();
  const pathId = useId();
  const dotFill =
    dotColor === 'emerald' ? 'fill-emerald-600' : 'fill-ink-700';
  const durSec = durationMs / 1000;

  return (
    <svg
      viewBox={viewBox}
      fill="none"
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <motion.path
        d={d}
        id={pathId}
        className={strokeClassName}
        strokeWidth={1}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={reduce ? { pathLength: 1 } : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={sectionViewport}
        transition={
          reduce
            ? { duration: 0 }
            : { duration: durSec, delay, ease: easeEditorial }
        }
      />
      {dot && !reduce && (
        <motion.circle
          r={2}
          className={dotFill}
          initial={{ opacity: 0, offsetDistance: '0%' } as never}
          whileInView={
            { opacity: [0, 1, 1, 0], offsetDistance: '100%' } as never
          }
          viewport={sectionViewport}
          transition={{
            duration: durSec,
            delay,
            ease: easeEditorial,
            times: [0, 0.08, 0.92, 1],
          }}
          style={{ offsetPath: `path('${d}')` } as never}
        />
      )}
    </svg>
  );
}
